import {
  CardViewEnum,
  MarketAccountStatusEnum,
  MarketAccountTypeEnum,
  type IPredictionOutcomeEvent,
  type MarketAccount,
  type PredictionMarketCardConfig,
  type Security,
} from '@talos/kyoko';

export { resolvePredictionCardLogoMarket } from '@talos/kyoko';

/**
 * MDS / status scope for a prediction card — same contract as normal market cards:
 * only explicitly selected market accounts. `config.market` is an event-filter venue, not MDS scope.
 */
export function getPredictionCardMarketDataScope(config: Pick<PredictionMarketCardConfig, 'marketAccounts'>): {
  markets: string[];
  marketAccounts: string[];
} {
  return {
    markets: [],
    marketAccounts: config.marketAccounts ? [...config.marketAccounts] : [],
  };
}

/** Prefer the saved market when still supported; otherwise prefer Kalshi if present, else the sole venue. */
export function resolveInitialPredictionMarket(
  preferred: string | undefined,
  supportedMarkets: string[]
): string | undefined {
  if (preferred && supportedMarkets.includes(preferred)) {
    return preferred;
  }
  const kalshiMarket = supportedMarkets.find(market => market === 'kalshi');
  if (kalshiMarket) {
    return kalshiMarket;
  }
  if (supportedMarkets.length === 1) {
    return supportedMarkets[0];
  }
  return undefined;
}

/**
 * The venue implied by a Market Account selection. Selections hold market account names, or a market
 * name when a whole market is selected, and only pin a venue when every selection agrees on one.
 */
export function resolvePredictionMarketFromMarketAccounts(
  selections: readonly string[],
  marketAccountsByName: Map<string, MarketAccount>
): string | undefined {
  const markets = new Set(selections.map(selection => marketAccountsByName.get(selection)?.Market ?? selection));
  return markets.size === 1 ? [...markets][0] : undefined;
}

/**
 * The venue to show for an Outcome that was chosen elsewhere (priming, modify, quick list), so the
 * Market field agrees with the Outcome instead of keeping whatever venue was defaulted.
 */
export function resolvePredictionMarketForOutcome(
  outcome: Security,
  currentMarket: string | undefined,
  supportedMarkets: readonly string[]
): string | undefined {
  if (currentMarket && outcome.Markets?.includes(currentMarket)) {
    return currentMarket;
  }
  return outcome.Markets?.find(market => supportedMarkets.includes(market)) ?? currentMarket;
}

/** Active trading market accounts for the selected prediction venue. */
export function getPredictionMarketAccountsForMarket(
  market: string | undefined,
  marketAccountsByMarket: Map<string, MarketAccount[]>
): MarketAccount[] {
  if (!market) {
    return [];
  }

  return (marketAccountsByMarket.get(market) ?? []).filter(
    account => account.Type === MarketAccountTypeEnum.Trading && account.Status === MarketAccountStatusEnum.Active
  );
}

/** Keep preferred accounts that are still valid; otherwise default to the sole account when only one exists. */
export function resolvePredictionMarketAccounts(
  preferredNames: readonly string[] | undefined,
  options: MarketAccount[]
): string[] {
  if (options.length === 0) {
    return [];
  }

  const optionNames = new Set(options.map(account => account.Name));
  const kept = (preferredNames ?? []).filter(name => optionNames.has(name));
  if (kept.length > 0) {
    return kept;
  }

  if (options.length === 1) {
    return [options[0]!.Name];
  }

  return [];
}

export function isPredictionOutcomeAvailableOnMarket(outcome: Security, market: string | undefined): boolean {
  if (!market) {
    return true;
  }

  return outcome.Markets?.includes(market) === true;
}

/** Outcomes available for Outcome-first selection, scoped to the selected market when one is set. */
export function filterPredictionOutcomesByMarket(outcomes: Security[], market: string | undefined): Security[] {
  return outcomes.filter(outcome => isPredictionOutcomeAvailableOnMarket(outcome, market));
}

/**
 * Map committed form values (an Event must be selected) onto a card config: carries over
 * unrelated config fields, and clears outcomeSymbol when no Outcome is selected.
 * Shared by card creation (PredictionCard) and settings save (PredictionCardSettings).
 */
export function buildPredictionMarketCardConfig(
  config: PredictionMarketCardConfig,
  values: {
    market: string | undefined;
    event: IPredictionOutcomeEvent;
    outcome: Security | undefined;
    selectedMarketAccounts: string[];
    selectedAggregation: string | undefined;
  }
): PredictionMarketCardConfig {
  const { outcomeSymbol: _removed, selectedAggregation: _removedAggregation, ...configWithoutOutcome } = config;
  const outcomeSymbol = values.outcome?.Symbol;

  return {
    ...configWithoutOutcome,
    market: values.market,
    marketAccounts: values.selectedMarketAccounts,
    ...(values.selectedAggregation ? { selectedAggregation: values.selectedAggregation } : {}),
    eventID: values.event.EventID,
    eventSymbol: values.event.EventSymbol,
    eventTitle: values.event.EventName,
    view: values.outcome ? CardViewEnum.COMPACT : CardViewEnum.NORMAL,
    ...(outcomeSymbol ? { outcomeSymbol } : {}),
    // A new event/outcome has a different page set; keep the old page only when identity is unchanged.
    ...(config.eventID !== values.event.EventID || config.outcomeSymbol !== outcomeSymbol
      ? { activePageIndex: 0 }
      : {}),
  };
}
